import {createBrowserHistory, BrowserHistory} from "history"
import {addContext, parseLocation, Location, RouteContext} from "./utils/context"

import {getQuery, getSearchString} from "./searchString"

export {getQuery, getSearchString}
export {addContext, appOrContext} from "./utils/context"
export {useAppRouteContext} from "./routing/ContextRouteProvider"

const browserHistory = createBrowserHistory()
export const defaultContext: RouteContext = {appPath: ""}

// hacky over-ride, looks good from outside though
export interface PBHistory extends BrowserHistory {
  push(location: Location, context?: RouteContext, options?: {scrollToTop: boolean}): any
  replace(location: Location, context?: RouteContext, options?: {scrollToTop: boolean}): any
  /** v4 compatibility shim — use instead of the removed `history.goBack()` */
  goBack(): void
}

const history: PBHistory = browserHistory as any
const push = browserHistory.push.bind(browserHistory)
const replace = browserHistory.replace.bind(browserHistory)

history.goBack = () => browserHistory.back()

const isRouteContext = (val: any): val is RouteContext => val != null && (typeof val === "string" || typeof val.appPath === "string")

history.push = (location: Location, context?: RouteContext | any, options = {scrollToTop: true}) => {
  if (!isRouteContext(context) && context != null) {
    // Called by react-router internally with (path, state) signature
    push(location as any, context)
    return
  }
  const ctx = isRouteContext(context) ? context : defaultContext
  const next = addContext(parseLocation(location), ctx)
  // history's push only reads `state` from its second argument; any `state` nested in the
  // location object is dropped. Forward it explicitly so navigation state survives.
  push(next as any, typeof next === "object" ? next?.state : undefined)
  const {scrollToTop} = options
  if (scrollToTop) {
    window.scrollTo(0, 0)
  }
}

history.replace = (location: string, context?: RouteContext | any, options = {scrollToTop: true}) => {
  if (!isRouteContext(context) && context != null) {
    // Called by react-router internally with (path, state) signature
    replace(location as any, context)
    return
  }
  const ctx = isRouteContext(context) ? context : defaultContext
  const next = addContext(parseLocation(location), ctx)
  // history's replace only reads `state` from its second argument; any `state` nested in the
  // location object is dropped. Forward it explicitly so navigation state survives.
  replace(next as any, typeof next === "object" ? next?.state : undefined)
  const {scrollToTop} = options
  if (scrollToTop) {
    window.scrollTo(0, 0)
  }
}

// Notify BrowserRouter about navigations from the custom history singleton.
// BrowserRouter only listens to popstate; pushState/replaceState alone won't trigger re-renders.
browserHistory.listen(({action}) => {
  if (action === "PUSH" || action === "REPLACE") {
    // Use queueMicrotask to fire after the current call stack completes,
    // avoiding interference with React Router's internal state updates
    queueMicrotask(() => {
      window.dispatchEvent(new PopStateEvent("popstate"))
    })
  }
})

export default history
